// @flow
import React, { useState, useMemo } from 'react';
import { toLonLat } from 'ol/proj';
import { getDistance } from 'ol/sphere';
import GeometryType from 'ol/geom/GeometryType';
import type { Zone as ZoneType } from '../../../../types';
import Feature from '../../../ui/openlayers/Feature';
import { createGeometry } from '../../../../utils/zoneUtils';
import Controls from './Controls';

const getNEPoint = (extent) => [Math.max(extent[0], extent[2]), Math.max(extent[1], extent[3])];

const getControlsPosition = (geometry) => {
    const type = geometry.getType();
    const extent = geometry.getExtent();
    let nePoint = getNEPoint(extent);

    if(type === GeometryType.CIRCLE) {
        return geometry.getClosestPoint(nePoint);
    }
    else {
        nePoint = toLonLat(nePoint);
        let coordinates = geometry.getCoordinates();

        if(type === GeometryType.POLYGON) {
            coordinates = coordinates[0];
        }

        let closestPoint;
        let closestDistance;
        for(let i = 0, length = coordinates.length; i < length; i += 1) {
            const point = coordinates[i];
            const distance = getDistance(toLonLat(point), nePoint);

            if(typeof closestDistance === 'undefined' || distance < closestDistance) {
                closestPoint = point;
                closestDistance = distance;
            }
        }

        return closestPoint;
    }
};

export type Props = $ReadOnly<{|
    id: string,
    zone: ZoneType,
    dispatch?: any
|}>;

function Zone({ zone }: Props) {
    const [selected, setSelected] = useState(false);
    const geometry = useMemo(() => createGeometry(zone.feature.type, zone.feature), [zone.feature]);
    const controlsPosition = useMemo(() => selected && getControlsPosition(geometry), [selected, geometry]);
    const properties = useMemo(() => ({
        type: zone.type,
        id: zone.id,
        active: zone.active
    }), [zone]);

    const onSelect = () => setSelected(true);
    const onDeselect = () => setSelected(false);

    return (
        <React.Fragment>
            {selected &&
                <Controls
                    position={controlsPosition}
                    zone={zone}
                />
            }

            <Feature
                geometry={geometry}
                properties={properties}
                onSelect={onSelect}
                onDeselect={onDeselect}
            />
        </React.Fragment>
    );
}

export default Zone;